import { Link } from 'react-router';
import { getUserOccupation } from '~/modules/user';
import { useConversation } from '../../../context';
import type { DirectConversation, Participant } from '../../../types';
import { getParticipantName } from '../../../utils';
import { ParticipantAvatar } from '../../avatar/ParticipantAvatar';
import { ChatBoxSettingsRemoveButton } from './ChatBoxSettingsRemoveButton';
import { ChatBoxSettingsMembersDropdown } from './ChatBoxSettingsMembersDropdown';

/**
 * The list of participants in the chat box conversation settings "members" tab.
 * @see {@link ChatBoxSettingsMembers}
 */
export function ChatBoxSettingsMembersList({
    conversation,
    isDisabled = false,
    onRemoveParticipant,
}: {
    conversation: DirectConversation;
    isDisabled?: boolean;
    onRemoveParticipant?: (participantID: string | number) => void;
}) {
    const showRemoveButton =
        conversation.IsGroupChat && conversation.Participants.length > 2;

    return (
        <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {conversation.Participants.map((participant) => (
                <ChatBoxSettingsMembersListRow
                    key={participant.ID}
                    participant={participant}
                    showRemoveButton={showRemoveButton}
                    isDisabled={isDisabled}
                    onRemoveParticipant={onRemoveParticipant}
                />
            ))}
        </ul>
    );
}

function ChatBoxSettingsMembersListRow({
    participant,
    showRemoveButton,
    isDisabled,
    onRemoveParticipant,
}: {
    participant: Participant;
    showRemoveButton: boolean;
    isDisabled: boolean;
    onRemoveParticipant?: (participantID: string | number) => void;
}) {
    const { conversationID, selfParticipant } = useConversation();
    const participantName =
        getParticipantName(participant) +
        (participant.ID === selfParticipant?.ID ? ' (you)' : '');

    return (
        <li
            key={participant.ID}
            className="group flex items-center gap-[8px] rounded-full p-[8px] hover:bg-black/10"
        >
            <ParticipantAvatar
                className="size-[36px]"
                fallbackClassName="!text-[14px]"
                participant={participant.Identity}
                showUserStatus
            />
            <div className="min-w-0 flex-1 space-y-[2px]">
                {participant.Identity.__typename === 'Professional' &&
                participant.Identity.Link ? (
                    <Link
                        to={participant.Identity.Link}
                        className="block max-w-fit truncate text-[14px] font-medium hover:underline"
                    >
                        {participantName}
                    </Link>
                ) : (
                    <div className="truncate text-[14px] font-medium leading-tight">
                        {participantName}
                    </div>
                )}
                <div className="truncate text-[12px] leading-tight opacity-70">
                    {participant.Identity.__typename === 'Professional'
                        ? participant.Identity.PrimaryCategory?.PrettyTitle
                        : getUserOccupation(participant.Identity)}
                </div>
            </div>
            {showRemoveButton && participant.ID !== selfParticipant?.ID && (
                <ChatBoxSettingsRemoveButton
                    className="invisible group-hover:visible"
                    participant={participant}
                    disabled={isDisabled}
                    onConfirm={() => {
                        onRemoveParticipant?.(participant.ID);
                    }}
                />
            )}
            {participant.ID !== selfParticipant?.ID && (
                <ChatBoxSettingsMembersDropdown
                    className="invisible group-hover:visible"
                    conversationID={conversationID}
                    participant={participant}
                />
            )}
        </li>
    );
}
